import {
  formatAmount,
  formatAmountWithUnit,
  isCatalogItemId,
  RESULT_RANGE,
} from "@loot-os/api-contract";
import { Badge } from "@loot-os/ui/components/badge";
import { BoxImage } from "@loot-os/ui/components/box-image";
import { Button } from "@loot-os/ui/components/button";
import { Card, CardContent } from "@loot-os/ui/components/card";
import { Checkbox } from "@loot-os/ui/components/checkbox";
import { Input } from "@loot-os/ui/components/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@loot-os/ui/components/select";
import { toast } from "@loot-os/ui/components/sonner";
import { Switch } from "@loot-os/ui/components/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@loot-os/ui/components/table";
import { Tooltip, TooltipContent, TooltipTrigger } from "@loot-os/ui/components/tooltip";
import { formatNumber } from "@loot-os/ui/lib/format";
import { cn } from "@loot-os/ui/lib/utils";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Copy,
  ImageOff,
  Info,
  Plus,
  Trash2,
  Upload,
  X,
} from "lucide-react";
import { useState } from "react";
import {
  amountColumnHeader,
  amountInputStep,
  amountInputValue,
  useCurrency,
} from "../lib/currency";
import {
  type DraftRow,
  type DropTableDraft,
  MARGIN_RANGE_PERCENT,
  percentToPpm,
  ppmToPercentInput,
  priceRangeAtMargins,
  repickPrompt,
  rowHasItem,
  rowItemId,
  splitRepeatedRows,
  takenItemIds,
} from "../lib/dropTableDraft";
import {
  DEFAULT_VIEW,
  type DropTableView,
  nextSort,
  type SortKey,
  type SourceFilter,
  viewRows,
} from "../lib/dropTableSort";
import { trpcClient } from "../lib/trpc";
import { BoxPricingFields } from "./BoxPricingFields";
import { CatalogItemPicker } from "./CatalogItemPicker";
import { DropTableImportDialog } from "./DropTableImportDialog";
import { DropTableSims } from "./DropTableSims";
import { ListingPicker } from "./ListingPicker";

const SOURCE_FILTER_LABELS: Record<SourceFilter, string> = {
  all: "All sources",
  CATALOG: "Catalog",
  MARKETPLACE: "Marketplace",
};

/**
 * A number cell's input, flush right under its right-aligned header: `block` so `ml-auto`
 * applies, and no spinner, which would hold the digits away from the right edge.
 */
const NUMBER_CELL_INPUT =
  "ml-auto block h-8 text-right [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none";

/** The first eight characters of an id, the part a person reads, with the rest a click away. */
function IdCell({ id }: { id: string }) {
  if (!id) return <span className="text-muted-foreground">—</span>;
  return (
    <button
      type="button"
      className="inline-flex items-center gap-1 font-mono text-xs text-muted-foreground hover:text-foreground"
      title={`${id} — click to copy`}
      onClick={() => {
        void navigator.clipboard?.writeText(id).then(() => toast.success("Id copied"));
      }}
    >
      {id.slice(0, 8)}
      <Copy className="size-3" />
    </button>
  );
}

function SortHeader({
  label,
  sortKey,
  view,
  onSort,
  className,
  hint,
}: {
  label: string;
  sortKey: SortKey;
  view: DropTableView;
  onSort: (key: SortKey) => void;
  className?: string;
  /** What the column means, in a tooltip on an info icon beside the label. */
  hint?: string;
}) {
  const active = view.sort?.key === sortKey ? view.sort.direction : null;
  const Icon = active === "asc" ? ArrowUp : active === "desc" ? ArrowDown : ArrowUpDown;
  return (
    <TableHead
      className={className}
      aria-sort={active === "asc" ? "ascending" : active === "desc" ? "descending" : "none"}
    >
      <span className="inline-flex items-center gap-1">
        <button
          type="button"
          className="inline-flex items-center gap-1 hover:text-foreground"
          onClick={() => onSort(sortKey)}
        >
          {label}
          <Icon className={active ? "size-3.5" : "size-3.5 opacity-40"} />
        </button>
        {hint && (
          <Tooltip>
            {/* Its own button, so the hint is reachable by keyboard and a click never sorts. */}
            <TooltipTrigger
              render={
                <button
                  type="button"
                  aria-label={`About ${label}`}
                  className="inline-flex text-muted-foreground hover:text-foreground"
                />
              }
            >
              <Info className="size-3.5" />
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">{hint}</TooltipContent>
          </Tooltip>
        )}
      </span>
    </TableHead>
  );
}

/** The row's processor stamps: limited stock, boxed's gem substitute and the stock exclusion. */
function RowFlags({ row }: { row: DraftRow }) {
  const flags = row.flags;
  if (!flags) return null;
  return (
    <span className="flex flex-wrap gap-1">
      {flags.isLimited && (
        <Badge variant="outline" className="text-[10px]">
          Limited{flags.limitedQuantity !== null ? ` · ${formatNumber(flags.limitedQuantity)}` : ""}
        </Badge>
      )}
      {flags.isExcluded && (
        <Badge
          variant="outline"
          className="text-[10px]"
          title="Out of stock: rolled at 0% until restocked, its weight spread across the rest"
        >
          Excluded
        </Badge>
      )}
      {flags.isGemSubstitute && !flags.isExcluded && (
        <Badge variant="outline" className="text-[10px]" title="Written by the processor">
          Gem sub
        </Badge>
      )}
    </span>
  );
}

/**
 * The drop table tab: the figures the table adds up to, the pricing controls, the table itself
 * and its simulations. Every edit goes into the box's draft; the header's Publish writes it.
 *
 * Sorting and filtering are a view over the rows (`lib/dropTableSort.ts`): the draft keeps its
 * own order, so a sort never changes what is published or what undo restores.
 */
export function DropTableEditor({
  draft,
  readOnly,
  sharedOnly,
}: {
  draft: DropTableDraft;
  /** Viewer mode or an archived box: the table renders, nothing edits. */
  readOnly?: boolean;
  /** Box templates pick from shared catalog items and shared listings only. */
  sharedOnly: boolean;
}) {
  const {
    rows,
    setRows,
    setWeight,
    setValueOverride,
    pickItem,
    pickListing,
    addCatalogRow,
    addListingRow,
    removeRow,
    removeRows,
    parsed,
    weightSum,
    sumOk,
    ev,
    stdDev,
    price,
    itemsOk,
    legacyRowCount,
    unresolvedRowCount,
    duplicateKeys,
    priceMode,
    dynamicKeys,
    toggleDynamic,
  } = draft;
  const currency = useCurrency();
  const [normalizing, setNormalizing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [view, setView] = useState<DropTableView>(DEFAULT_VIEW);
  // Row keys ticked for a bulk remove. Read through the current rows, so a key an undo or a
  // remove took away never counts.
  const [ticked, setTicked] = useState<ReadonlySet<string>>(new Set());
  const solverColumn = priceMode === "targetPrice";
  const shown = viewRows(rows, view, currency);
  const [lowPrice, highPrice] = priceRangeAtMargins(ev);
  const selectedKeys = new Set(rows.filter((row) => ticked.has(row.key)).map((row) => row.key));
  // The header box selects what the filter shows, not rows the filter hides.
  const shownSelected = shown.filter((row) => selectedKeys.has(row.key)).length;
  const allShownSelected = shown.length > 0 && shownSelected === shown.length;
  const toggleRow = (key: string, checked: boolean) =>
    setTicked((prev) => {
      const next = new Set(prev);
      if (checked) next.add(key);
      else next.delete(key);
      return next;
    });
  const toggleShown = () =>
    setTicked((prev) => {
      const next = new Set(prev);
      for (const row of shown) {
        if (allShownSelected) next.delete(row.key);
        else next.add(row.key);
      }
      return next;
    });
  const removeSelected = () => {
    const count = selectedKeys.size;
    removeRows(selectedKeys);
    setTicked(new Set());
    toast.success(
      `Removed ${formatNumber(count)} ${count === 1 ? "row" : "rows"} — undo brings them back`,
    );
  };
  const unpickedCount = legacyRowCount + unresolvedRowCount;
  const tableIssue: { text: string; tone: "error" | "info" } | null =
    duplicateKeys.size > 0
      ? {
          tone: "error",
          text: `${formatNumber(duplicateKeys.size)} ${duplicateKeys.size === 1 ? "row repeats an item" : "rows repeat items"} (marked Repeated): remove it, or move its weight onto the other row.`,
        }
      : unpickedCount > 0
        ? {
            tone: "info",
            text: `${formatNumber(unpickedCount)} ${unpickedCount === 1 ? "row needs" : "rows need"} a catalog item before publishing: the old name or pasted id is shown on the row.`,
          }
        : !itemsOk
          ? { tone: "info", text: "Every row needs a catalog item or a listing before publishing." }
          : null;
  const amount = (cents: number) => formatAmountWithUnit(Math.round(cents), currency);

  // A plain query, not a mutation, so the query client's toast doesn't cover it.
  const normalize = async () => {
    setNormalizing(true);
    try {
      const weights = rows.map((r) => percentToPpm(r.weight));
      const result = await trpcClient.boxes.normalizeWeights.query({ weights });
      setRows((prev) =>
        prev.map((row, i) => ({ ...row, weight: ppmToPercentInput(result.weights[i] ?? 0) })),
      );
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "normalize failed");
    } finally {
      setNormalizing(false);
    }
  };

  return (
    <>
      <Card className="sticky top-0 z-10">
        <CardContent className="flex flex-col gap-4">
          <dl className="flex flex-wrap items-end gap-x-8 gap-y-3 text-sm">
            <div>
              <dt className="text-muted-foreground">Total weight</dt>
              <dd>
                <Badge
                  variant={sumOk ? "outline" : "destructive"}
                  className={
                    sumOk ? "border-emerald-500/50 bg-emerald-500/10 text-emerald-400" : ""
                  }
                  title={`${formatNumber(weightSum)} of ${formatNumber(RESULT_RANGE)}`}
                >
                  {ppmToPercentInput(weightSum)}% / 100%
                </Badge>
              </dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Expected value</dt>
              <dd className="font-mono">{amount(ev)}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Std deviation</dt>
              <dd className="font-mono">{amount(stdDev)}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Price</dt>
              <dd className="font-mono">
                {price !== null && price > 0 ? amount(price) : "—"}
                {price !== null && price > 0 && ev > 0 && (
                  <span className="ml-2 text-xs text-muted-foreground">
                    returns {((ev / price) * 100).toFixed(1)}%
                  </span>
                )}
              </dd>
            </div>
            <div>
              <dt className="text-muted-foreground">
                Price at {MARGIN_RANGE_PERCENT[0]}–{MARGIN_RANGE_PERCENT[1]}% margin
              </dt>
              <dd className="font-mono">
                {ev > 0 ? `${amount(lowPrice)} – ${amount(highPrice)}` : "—"}
              </dd>
            </div>
          </dl>
          <BoxPricingFields draft={draft} readOnly={readOnly} />
        </CardContent>
      </Card>

      <Card>
        <CardContent className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addCatalogRow}
              disabled={readOnly}
            >
              <Plus />
              Add item
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addListingRow}
              disabled={readOnly}
            >
              <Plus />
              Add listing
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setImporting(true)}
              disabled={readOnly}
            >
              <Upload />
              Import
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => void normalize()}
              // Scaling to the full range leaves the solver no weight to hand the two rows it moves.
              disabled={readOnly || normalizing || solverColumn}
              aria-busy={normalizing}
              title={
                solverColumn
                  ? "The solver sets the weights under a target price"
                  : "Scale every weight so they total exactly 100%"
              }
            >
              {/* A fixed label: a wider "Normalizing…" would push the button beside it. */}
              Normalize
            </Button>
            {/* Always here, so ticking a row never reflows the toolbar; off with nothing ticked.
                It is the last button before the right-aligned filters, so its label growing with
                the count moves nothing else. The header checkbox clears a selection. */}
            <Button
              type="button"
              variant="destructive"
              size="sm"
              onClick={removeSelected}
              disabled={readOnly || selectedKeys.size === 0}
            >
              <Trash2 />
              {selectedKeys.size > 0
                ? `Remove ${formatNumber(selectedKeys.size)} selected`
                : "Remove selected"}
            </Button>
            <div className="ml-auto flex flex-wrap items-center gap-2">
              <Input
                className="h-8 w-48"
                placeholder="Filter by name or id"
                value={view.search}
                onChange={(e) => setView((v) => ({ ...v, search: e.target.value }))}
              />
              <Select
                value={view.source}
                items={SOURCE_FILTER_LABELS}
                onValueChange={(value) => setView((v) => ({ ...v, source: value as SourceFilter }))}
              >
                <SelectTrigger className="h-8 w-36" size="sm" aria-label="Source">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(SOURCE_FILTER_LABELS).map(([value, label]) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Fixed layout: columns take their header widths and Item the rest, so a pick filling
              in a row never re-balances them; below the minimum width the table scrolls. */}
          <Table className="min-w-[56rem] table-fixed">
            <TableHeader>
              <TableRow>
                {/* The first column also carries the card's own padding (--card-spacing), so its
                    width is that plus the 16px box and a gap before the Item column. */}
                <TableHead className="w-[calc(var(--card-spacing)+1.75rem)]">
                  <Checkbox
                    aria-label="Select every row shown"
                    checked={allShownSelected}
                    indeterminate={shownSelected > 0 && !allShownSelected}
                    onCheckedChange={toggleShown}
                    disabled={readOnly || shown.length === 0}
                  />
                </TableHead>
                <SortHeader
                  label="Item"
                  sortKey="name"
                  view={view}
                  onSort={(key) => setView((v) => ({ ...v, sort: nextSort(v.sort, key) }))}
                  className="w-auto"
                />
                {/* Fixed widths: a value filling in on a pick must not resize the columns. */}
                <TableHead className="w-28">Id</TableHead>
                <SortHeader
                  label={amountColumnHeader("Market price", currency)}
                  sortKey="marketPrice"
                  hint="What it sells for elsewhere. For comparison only."
                  view={view}
                  onSort={(key) => setView((v) => ({ ...v, sort: nextSort(v.sort, key) }))}
                  className="w-32 text-right"
                />
                <SortHeader
                  label={amountColumnHeader("Value", currency)}
                  sortKey="value"
                  hint="What a win is worth. Sets the EV and box price."
                  view={view}
                  onSort={(key) => setView((v) => ({ ...v, sort: nextSort(v.sort, key) }))}
                  className="w-28 text-right"
                />
                <TableHead className="w-32 text-right">
                  {amountColumnHeader("Override", currency)}
                </TableHead>
                <SortHeader
                  label="Weight %"
                  sortKey="weight"
                  view={view}
                  onSort={(key) => setView((v) => ({ ...v, sort: nextSort(v.sort, key) }))}
                  className="w-36 text-right"
                />
                {solverColumn && <TableHead className="w-20 text-center">Solver</TableHead>}
                <TableHead className="w-10" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {shown.map((row) => (
                <TableRow
                  key={row.key}
                  data-state={selectedKeys.has(row.key) ? "selected" : undefined}
                >
                  <TableCell>
                    <Checkbox
                      aria-label={`Select ${row.name || "row"}`}
                      checked={selectedKeys.has(row.key)}
                      onCheckedChange={(checked) => toggleRow(row.key, checked)}
                      disabled={readOnly}
                    />
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <BoxImage
                        src={row.imageUrl}
                        className="size-12 shrink-0 rounded-md border object-contain"
                        fallback={<ImageOff className="size-4 text-muted-foreground" />}
                      />
                      <div className="flex min-w-0 flex-col gap-1">
                        {row.itemSource === "MARKETPLACE" ? (
                          <ListingPicker
                            value={row.listingId ? row : null}
                            sharedOnly={sharedOnly}
                            taken={takenItemIds(rows, "MARKETPLACE", row.key)}
                            disabled={readOnly}
                            onPick={(listing) => pickListing(row.key, listing)}
                          />
                        ) : (
                          <CatalogItemPicker
                            value={isCatalogItemId(row.catalogItemId) ? row : null}
                            placeholder={repickPrompt(row)}
                            sharedOnly={sharedOnly}
                            taken={takenItemIds(rows, "CATALOG", row.key)}
                            disabled={readOnly}
                            onPick={(item) => pickItem(row.key, item)}
                          />
                        )}
                        <span className="flex items-center gap-1">
                          {row.itemSource === "MARKETPLACE" && (
                            <Badge variant="secondary" className="text-[10px]">
                              Listing
                            </Badge>
                          )}
                          <RowFlags row={row} />
                          {duplicateKeys.has(row.key) && (
                            <Badge
                              variant="destructive"
                              className="text-[10px]"
                              title="Another row names this item. Remove one, or move its weight onto the other."
                            >
                              Repeated
                            </Badge>
                          )}
                        </span>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <IdCell id={rowHasItem(row) ? rowItemId(row) : ""} />
                  </TableCell>
                  <TableCell className="text-right font-mono text-sm">
                    {row.marketPriceCents !== null
                      ? formatAmount(row.marketPriceCents, currency)
                      : "—"}
                  </TableCell>
                  <TableCell className="text-right font-mono text-sm">
                    {rowHasItem(row) ? formatAmount(row.value, currency) : "—"}
                  </TableCell>
                  <TableCell>
                    {row.itemSource === "MARKETPLACE" ? (
                      <p className="text-right font-mono text-sm text-muted-foreground">—</p>
                    ) : (
                      <Input
                        className={`${NUMBER_CELL_INPUT} w-24`}
                        type="number"
                        min={amountInputStep(currency)}
                        step={amountInputStep(currency)}
                        placeholder="—"
                        disabled={readOnly}
                        value={
                          row.valueOverride ??
                          (row.storedOverrideCents != null
                            ? amountInputValue(row.storedOverrideCents, currency)
                            : "")
                        }
                        onChange={(e) => setValueOverride(row.key, e.target.value)}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <Input
                      className={`${NUMBER_CELL_INPUT} w-28`}
                      type="number"
                      min={0}
                      max={100}
                      step={0.0001}
                      value={row.weight}
                      disabled={readOnly}
                      title={`${formatNumber(percentToPpm(row.weight))} of ${formatNumber(RESULT_RANGE)}`}
                      onChange={(e) => setWeight(row.key, e.target.value)}
                    />
                  </TableCell>
                  {solverColumn && (
                    <TableCell className="text-center">
                      <Switch
                        aria-label="Let the solver set this weight"
                        checked={dynamicKeys.includes(row.key)}
                        disabled={readOnly}
                        onCheckedChange={() => toggleDynamic(row.key)}
                      />
                    </TableCell>
                  )}
                  <TableCell>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      onClick={() => removeRow(row.key)}
                      disabled={readOnly || rows.length === 1}
                      title="Remove item (undo brings it back)"
                    >
                      <X />
                      <span className="sr-only">Remove item</span>
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {shown.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={solverColumn ? 9 : 8}
                    className="text-center text-sm text-muted-foreground"
                  >
                    No rows match the filter.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          {/* One line of fixed height for the count and the one thing still in the way of a
              publish, so a message coming or going never moves the simulations below. */}
          <div className="flex h-5 items-center justify-between gap-4 text-xs text-muted-foreground">
            <p className="shrink-0">
              {formatNumber(rows.length)} {rows.length === 1 ? "row" : "rows"}
              {shown.length !== rows.length && ` · ${formatNumber(shown.length)} shown`}
              {view.sort && " · sorted for viewing; publishing keeps the table's own order"}
            </p>
            {tableIssue && (
              <p
                className={cn(
                  "min-w-0 truncate text-sm",
                  tableIssue.tone === "error" && "text-destructive",
                )}
                title={tableIssue.text}
              >
                {tableIssue.text}
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      <DropTableSims items={parsed} price={price} />

      <DropTableImportDialog
        open={importing}
        onOpenChange={setImporting}
        sharedOnly={sharedOnly}
        onImport={(imported, mode) => {
          // A replace drops the one blank row a new box starts with; an append keeps real rows,
          // and skips an imported item the table already names rather than adding it twice.
          const kept = mode === "replace" ? [] : rows.filter((row) => rowHasItem(row) || row.name);
          const { added, repeated } = splitRepeatedRows(kept, imported);
          setRows(() => [...kept, ...added]);
          if (repeated.length > 0) {
            toast.warning(
              `Skipped ${formatNumber(repeated.length)} ${repeated.length === 1 ? "row" : "rows"} already in the table: ${repeated.map((row) => row.name).join(", ")}`,
            );
          }
        }}
      />
    </>
  );
}
